import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { usePipedriveStore, usePopupStore } from "../..";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import ISaleComp from "../../interfaces/ISaleComp";
import PopupType from "../../interfaces/PopupType";
import type PipedriveStore from "../../store/PipedriveStore";
import { saleCompDealProfileToString, saleCompPropertyTypeToString, saleCompSourceToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import Grid, { IHeader } from "../Grid";

function SaleCompsGrid({ saleComps, additionalHeaders, radiusDropdown }: ISaleCompsGridProps) {
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const navigateToSaleComp = useNavigateToSaleComp();
    const headers = useMemo(() => buildSaleCompsHeaders(additionalHeaders), [additionalHeaders],);
    const downloadHeaders = useMemo(() => buildSaleCompsDownloadHeaders(pipedriveStore, headers), [pipedriveStore, headers],);

    return (
        <Grid
            data={saleComps}
            headers={headers}
            downloadHeaders={downloadHeaders}
            getId={item => item.id}
            downloadFileName={"Sale Comps"}
            onRowClick={item => navigateToSaleComp(item.id)}
            isRowActive={item => popupStore.type === PopupType.SaleComp && popupStore.id === item.id}
            header={
                <div className={[commonStyles.gridTitle, commonStyles.header].join(" ")}>
                    {saleComps.length} Sale Comps
                    {radiusDropdown}
                </div>
            }
        />
    );
}

export default observer(SaleCompsGrid);

interface ISaleCompsGridProps {
    saleComps: ISaleComp[];
    additionalHeaders?: IHeader<ISaleComp>[];
    radiusDropdown?: React.ReactNode;
}

const buildSaleCompsHeaders = (additionalHeaders?: IHeader<ISaleComp>[]): IHeader<ISaleComp>[] => {
    return [
        {
            align: "left",
            titleStrings: ["SALE PRICE"],
            getValue: item => item.salePrice,
            getLabel: item => item.salePrice ? formatPrice(item.salePrice) : "",
        },
        {
            align: "left",
            titleStrings: ["DATE SOLD"],
            getValue: item => item.saleDate,
            getLabel: item => item.saleDate ? formatDate(item.saleDate) : "",
        },
        {
            align: "left",
            isFullWidth: true,
            titleStrings: ["ADDRESS"],
            getValue: item => item.address,
            getLabel: item => item.address,
        },
        {
            titleStrings: ["ZONING"],
            getValue: item => item.zoning,
            getLabel: item => item.zoning || "",
        },
        {
            titleStrings: ["BUILDING", "SIZE"],
            getValue: item => item.buildingSize,
            getLabel: item => item.buildingSize ? formatNumber(item.buildingSize) : "",
        },
        {
            titleStrings: ["PARCEL SIZE", "(ACRES)"],
            getValue: item => item.parcelSizeAcres,
            getLabel: item => item.parcelSizeAcres ? formatNumber(item.parcelSizeAcres) : "",
        },
        {
            titleStrings: ["LAND SIZE", "(USABLE)"],
            getValue: item => item.landSizeUsable,
            getLabel: item => item.landSizeUsable ? formatNumber(item.landSizeUsable) : "",
        },
        {
            titleStrings: ["GROSS", "$/ACRE"],
            getValue: item => item.grossPerAcre,
            getLabel: item => item.grossPerAcre ? formatPrice(item.grossPerAcre) : "",
        },
        {
            titleStrings: ["PRICE PER", "USABLE ACRE"],
            getValue: item => item.pricePerUsableAcre,
            getLabel: item => item.pricePerUsableAcre ? formatPrice(item.pricePerUsableAcre) : "",
        },
        {
            titleStrings: ["BUILDING", "$/PSF"],
            getValue: item => item.buildingPerSqFt,
            getLabel: item => item.buildingPerSqFt ? formatPrice(item.buildingPerSqFt) : "",
        },
        ...(additionalHeaders ?? []),
    ];
};

const buildSaleCompsDownloadHeaders = (pipedriveStore: PipedriveStore, headers: IHeader<ISaleComp>[]): IHeader<ISaleComp>[] => {
    return [
        ...headers,
        {
            titleStrings: ["ID"],
            getValue: item => item.id,
            getLabel: item => String(item.id),
        },
        {
            titleStrings: ["REGRID ID"],
            getValue: item => item.regridId,
            getLabel: item => item.regridId || "",
        },
        {
            titleStrings: ["BUYER NAME"],
            getValue: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveBuyerId)?.name || item.buyerName || "",
            getLabel: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveBuyerId)?.name || item.buyerName || "",
        },
        {
            titleStrings: ["PIPEDRIVE", "BUYER ID"],
            getValue: item => item.pipedriveBuyerId,
            getLabel: item => item.pipedriveBuyerId === null || item.pipedriveBuyerId === undefined ? "" : String(item.pipedriveBuyerId),
        },
        {
            titleStrings: ["SELLER NAME"],
            getValue: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveSellerId)?.name || item.sellerName || "",
            getLabel: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveSellerId)?.name || item.sellerName || "",
        },
        {
            titleStrings: ["PIPEDRIVE", "SELLER ID"],
            getValue: item => item.pipedriveSellerId,
            getLabel: item => item.pipedriveSellerId === null || item.pipedriveSellerId === undefined ? "" : String(item.pipedriveSellerId),
        },
        {
            titleStrings: ["BROKERAGE NAME"],
            getValue: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveBrokerageId)?.name || item.brokerageName || "",
            getLabel: item => pipedriveStore.organizations.find(o => o.id === item.pipedriveBrokerageId)?.name || item.brokerageName || "",
        },
        {
            titleStrings: ["PIPEDRIVE", "BROKERAGE ID"],
            getValue: item => item.pipedriveBrokerageId,
            getLabel: item => item.pipedriveBrokerageId === null || item.pipedriveBrokerageId === undefined ? "" : String(item.pipedriveBrokerageId),
        },
        {
            titleStrings: ["PROPERTY TYPE"],
            getValue: item => item.propertyType,
            getLabel: item => item.propertyType ? saleCompPropertyTypeToString(item.propertyType) : "",
        },
        {
            titleStrings: ["DEAL PROFILE"],
            getValue: item => item.dealProfile,
            getLabel: item => item.dealProfile ? saleCompDealProfileToString(item.dealProfile) : "",
        },
        {
            titleStrings: ["NOI"],
            getValue: item => item.noi,
            getLabel: item => item.noi === null || item.noi === undefined ? "" : formatPrice(item.noi),
        },
        {
            titleStrings: ["CAP RATE"],
            getValue: item => item.capRate,
            getLabel: item => item.capRate === null || item.capRate === undefined ? "" : `${formatNumber(item.capRate)}%`,
        },
        {
            titleStrings: ["MARKETED"],
            getValue: item => item.isMarketed,
            getLabel: item => item.isMarketed ? "Yes" : "No",
        },
        {
            titleStrings: ["NOTES"],
            getValue: item => item.notes,
            getLabel: item => item.notes || "",
        },
        {
            titleStrings: ["CENTER LATITUDE"],
            getValue: item => item.centerPoint?.latitude,
            getLabel: item => item.centerPoint?.latitude === null || item.centerPoint?.latitude === undefined ? "" : String(item.centerPoint?.latitude),
        },
        {
            titleStrings: ["CENTER LONGITUDE"],
            getValue: item => item.centerPoint?.longitude,
            getLabel: item => item.centerPoint?.longitude === null || item.centerPoint?.longitude === undefined ? "" : String(item.centerPoint?.longitude),
        },
        {
            titleStrings: ["MODIFIED BY"],
            getValue: item => item.modifiedByUser,
            getLabel: item => item.modifiedByUser || "",
        },
        {
            titleStrings: ["MODIFIED DATE"],
            getValue: item => item.modifiedDate,
            getLabel: item => item.modifiedDate ? formatDateTime(item.modifiedDate) : "",
        },
        {
            titleStrings: ["COMP SOURCE"],
            getValue: item => item.compSource,
            getLabel: item => item.compSource ? saleCompSourceToString(item.compSource) : "",
        },
    ];
};
